import React, {useState, useEffect, useCallback} from 'react';
import classnames from 'classnames';
import Select from 'react-select';
import {Trans} from 'react-i18next';

/**
 * Component for setting product details.
 * @param {Object} form - Form-related properties (e.g., setFieldValue).
 * @param {Object} field - Field-related properties (e.g., name, value).
 * @param {Object[]} options - The options for product languages.
 * @param {string} [chooseLang=null] - Default language for the product.
 * @param {string} showProductLang - Text to show for product language label.
 * @param {string} showProductName - Text to show for product name label.
 * @param {string} showProductDesc - Text to show for product description label.
 * @param {string} labelWidth -label's width (Bootstrap total width is 12)
 * @param {string} placeholderName - placeholder content
 * @param {string} placeholderDesc - placeholder content
 */

export default function ProductSetting({
  form: {setFieldValue},
  field: {name, value},
  options = [],
  chooseLang = null,
  showProductLang,
  showProductName,
  showProductDesc,
  showBrandName,
  labelWidth = '4',
  placeholderName = '',
  placeholderDesc = '',
  placeholderBrandName = '',
  disabledBrandName,
}) {
  const [selectedOption, setSelectedOption] = useState(chooseLang);
  const product = value && value[selectedOption];
  const [productName, setProductName] = useState(
    product ? product.productName : ''
  );
  const [productDesc, setProductDesc] = useState(
    product ? product.productDesc : ''
  );
  const [brandName, setBrandName] = useState(product ? product.brandName : '');
  const labelClass = classnames(
    `col-${labelWidth}`,
    'col-form-label',
    'text-left'
  );
  const containerClass = classnames(`col-${12 - labelWidth}`);

  useEffect(() => {
    setSelectedOption(chooseLang);
  }, [chooseLang]);

  useEffect(() => {
    setProductName(product ? product.productName : '');
    setProductDesc(product ? product.productDesc : '');
    setBrandName(product ? product.brandName : '');
  }, [product, value]);

  const handleChange = useCallback(
    (itemName, itemValue) => {
      const updatedProduct = {...product, [itemName]: itemValue};
      setFieldValue(name, {...value, [selectedOption]: updatedProduct});
    },
    [name, value, selectedOption, product, setFieldValue]
  );

  return (
    <>
      <ProductNameInput
        options={options}
        chooseLang={selectedOption}
        setSelectedOption={setSelectedOption}
        productName={productName}
        setProductName={setProductName}
        handleChange={handleChange}
        showProductLang={showProductLang}
        showProductName={showProductName}
        labelClass={labelClass}
        containerClass={containerClass}
        placeholderName={placeholderName}
      />
      {showProductDesc && (
        <ProductDescriptionInput
          productDesc={productDesc}
          setProductDesc={setProductDesc}
          handleChange={handleChange}
          showProductDesc={showProductDesc}
          labelClass={labelClass}
          containerClass={containerClass}
          placeholderDesc={placeholderDesc}
        />
      )}
      {showBrandName && (
        <BrandNameInput
          brandName={brandName}
          setBrandName={setBrandName}
          handleChange={handleChange}
          showBrandName={showBrandName}
          labelClass={labelClass}
          containerClass={containerClass}
          placeholderBrandName={placeholderBrandName}
          disabledBrandName={disabledBrandName}
        />
      )}
    </>
  );
}

/**
 * Input component for product name selection.
 * @param {Object[]} options - The options for product languages.
 * @param {boolean} [isSearchable=false] - Whether the Select component is searchable.
 * @param {boolean} [isDisabled=false] - Whether the Select component is disabled.
 * @param {string} chooseLang - Editing product name in selected language
 * @param {Function} setSelectedOption - Callback to set the selected language option.
 * @param {string} productName - Current product name value.
 * @param {Function} setProductName - Callback to set the product name.
 * @param {Function} handleChange - Callback when the product name changes.
 * @param {string} showProductLang - Text to show for product language label.
 * @param {string} showProductName - Text to show for product name label.
 * @param {string} labelClass -label's width
 * @param {string} containerClass - content's width
 * @param {string} placeholderName - placeholder content
 */
const ProductNameInput = ({
  options,
  isSearchable = false,
  isDisabled = false,
  chooseLang,
  setSelectedOption,
  productName,
  setProductName,
  handleChange,
  showProductLang,
  showProductName,
  labelClass,
  containerClass,
  placeholderName = '',
}) => {
  return (
    <>
      <div className="form-group row">
        <label className={labelClass}>{showProductLang}</label>
        <div className={containerClass}>
          <Select
            value={options.find(o => o.value === chooseLang)}
            options={options}
            onChange={item => {
              setSelectedOption(item.value);
            }}
            isSearchable={isSearchable}
            isMulti={false}
            isDisabled={isDisabled}
            placeholder={
              <Trans i18nKey="c.c.select.placeholder">Select...</Trans>
            }
          />
        </div>
      </div>
      <div className="form-group row">
        <label className={labelClass}>
          {showProductName}
          <span className="required">*</span>
        </label>
        <div className={containerClass}>
          <input
            className="form-control"
            value={productName}
            onChange={e => {
              setProductName(e.target.value);
              handleChange('productName', e.target.value);
            }}
            placeholder={placeholderName}
          />
        </div>
      </div>
    </>
  );
};

/**
 * Input component for product description.
 * @param {string} productDesc - Current product description value.
 * @param {Function} setProductDesc - Callback to set the product description.
 * @param {Function} handleChange - Callback when the product description changes.
 * @param {string} showProductDesc - Text to show for product description label.
 * @param {string} labelClass -label's width
 * @param {string} containerClass - content's width
 * @param {string} placeholderDesc - placeholder content
 */

const ProductDescriptionInput = ({
  productDesc,
  setProductDesc,
  handleChange,
  showProductDesc,
  labelClass,
  containerClass,
  placeholderDesc = '',
}) => {
  return (
    <div className="form-group row">
      <label className={labelClass}>{showProductDesc}</label>
      <div className={containerClass}>
        <textarea
          className="form-control"
          value={productDesc}
          onChange={e => {
            setProductDesc(e.target.value);
            handleChange('productDesc', e.target.value);
          }}
          placeholder={placeholderDesc}
        />
      </div>
    </div>
  );
};

const BrandNameInput = ({
  brandName,
  setBrandName,
  handleChange,
  showBrandName,
  labelClass,
  containerClass,
  placeholderBrandName = '',
  disabledBrandName = false,
}) => {
  return (
    <div className="form-group row">
      <label className={labelClass}>{showBrandName}</label>
      <div className={containerClass}>
        <input
          className="form-control"
          value={brandName}
          onChange={e => {
            setBrandName(e.target.value);
            handleChange('brandName', e.target.value);
          }}
          placeholder={placeholderBrandName}
          disabled={disabledBrandName}
        />
      </div>
    </div>
  );
};
